import { useQuery } from '@tanstack/react-query';
import type { PageResponse } from '@models/api';
import { getEsimInventory, type EsimInventoryResponse } from '@apis/esim';

/**
 * Fetch a single eSIM inventory detail by ID.
 *
 * - Uses React Query for caching and request lifecycle management.
 * - Query is enabled only when a valid inventory ID is provided.
 * - Window focus refetch is disabled to prevent unintended refreshes
 *   while editing or viewing detail data.
 */
export const useInventoryDetail = (esimInventoryId: number) => {
  const enabled = Number.isFinite(esimInventoryId) && esimInventoryId > 0;

  return useQuery<PageResponse<EsimInventoryResponse>, Error>({
    /**
     * Query key includes the inventory ID to ensure
     * each inventory detail is cached independently.
     */
    queryKey: ['inventoryDetail', esimInventoryId],

    /**
     * API call to fetch inventory detail.
     */
    queryFn: () => getEsimInventory(esimInventoryId),

    /**
     * Prevents execution when the ID is invalid.
     */
    enabled,

    /**
     * Avoid refetching on window focus.
     * This is important for detail/edit pages to keep UI stable.
     */
    refetchOnWindowFocus: false,
  });
};
